// ─── PRODUCT STATE ────────────────────────────────────────────────────────────
//
// Extracted from configurator.html's inline script (Phase 5 split). Loaded via
// <script src> at the exact document position this code used to occupy inline
// -- must stay before pricing-engine.js and INIT, both of which depend on the
// globals declared here (CATEGORIES, MATERIALS, cartItems, artworkDataUrl, etc.).
//
// This is the app's core state: ArtworkStore (IndexedDB), the product-switcher
// chip catalog, per-product state save/restore, CATEGORIES/MATERIALS/JAR_COLORS,
// all the current* globals (cart, artwork, product selection), and packaging
// product size config. Nearly everything else in the app reads from here.
//
// Depends on globals declared earlier in configurator.html's own script:
// getSelectedSizeLabel() (vinylpac-utils.js), and later-defined functions it
// calls indirectly through user interaction (not at load time): updatePrice(),
// setQty(), selectMaterial(), setFinish(), setSpecialtyFinish(), switchCategory(),
// closeSizeQtyPopup(), buildSizeSelect(), buildQtySelect(), updateSizeTrigger(),
// updateQtyTrigger(), _cancelMaterialAnim(), setPreviewHint(), selectJarColor(),
// openSizePopup(), getSelectedSizeLabel(), pdfjsLib (CDN).

// ─── ARTWORK STORE (IndexedDB) ────────────────────────────────────────────────
// Stores full artwork blobs keyed by cart item ID.
// Unlike sessionStorage, IndexedDB handles large files and survives page reloads,
// so artwork is never lost when the user navigates or refreshes mid-session.
var ArtworkStore = (function() {
  var DB_NAME    = 'vinylpac_artwork';
  var DB_VERSION = 1;
  var STORE_NAME = 'artworks';
  var _db        = null;

  function open() {
    return new Promise(function(resolve, reject) {
      if (_db) { resolve(_db); return; }
      var req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = function(e) {
        e.target.result.createObjectStore(STORE_NAME, { keyPath: 'itemId' });
      };
      req.onsuccess  = function(e) { _db = e.target.result; resolve(_db); };
      req.onerror    = function()  { reject(req.error); };
    });
  }

  // Save artwork data URLs before a cart item is committed.
  // Stores all four artwork fields so checkoutSim can retrieve them after a page reload.
  function save(itemId, artworkUrl, artworkOriginalUrl, lidArtworkUrl, lidArtworkOriginalUrl) {
    return open().then(function(db) {
      return new Promise(function(resolve, reject) {
        var tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put({
          itemId:              itemId,
          artworkUrl:          artworkUrl          || null,
          artworkOriginalUrl:  artworkOriginalUrl  || null,
          lidArtworkUrl:       lidArtworkUrl       || null,
          lidArtworkOriginalUrl: lidArtworkOriginalUrl || null
        });
        tx.oncomplete = resolve;
        tx.onerror    = function() { reject(tx.error); };
        tx.onabort    = function() { reject(tx.error || new Error('Artwork storage transaction was cancelled.')); };
      });
    });
  }

  // Retrieve blobs for a cart item
  function get(itemId) {
    return open().then(function(db) {
      return new Promise(function(resolve, reject) {
        var req = db.transaction(STORE_NAME).objectStore(STORE_NAME).get(itemId);
        req.onsuccess = function() { resolve(req.result || null); };
        req.onerror   = function() { reject(req.error); };
      });
    });
  }

  // Delete a single item's blobs (called when item is removed from cart)
  function remove(itemId) {
    return open().then(function(db) {
      return new Promise(function(resolve, reject) {
        var tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).delete(itemId);
        tx.oncomplete = resolve;
        tx.onerror    = function() { reject(tx.error); };
        tx.onabort    = function() { reject(tx.error || new Error('Artwork storage transaction was cancelled.')); };
      });
    });
  }

  // Wipe all stored artwork (called on checkout success and clearCart)
  function clear() {
    return open().then(function(db) {
      return new Promise(function(resolve, reject) {
        var tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).clear();
        tx.oncomplete = resolve;
        tx.onerror    = function() { reject(tx.error); };
        tx.onabort    = function() { reject(tx.error || new Error('Artwork storage transaction was cancelled.')); };
      });
    });
  }

  return { save: save, get: get, remove: remove, clear: clear };
})();

// ─── DATA ───────────────────────────────────────────────────────────────────

// ─── PRODUCT SWITCHER ────────────────────────────────────────────────────────
// Flat catalog of all orderable products. Each chip maps to a category +
// optional product/nav overrides (same params as the collection page links).
// ─── PRODUCT SWITCHER ─────────────────────────────────────────────────────────
// Each nav section has its own group of chips. Packaging is built dynamically
// from the admin config; all other groups are static.
// chip shape: { id, cat, label, img, name?, nav? }
//   name  → sets _customName on the category (product-specific override)
//   nav   → which top nav link to highlight (when cat differs from nav section)

// Product variants per nav section — mirrors the collection pages exactly.
// Each entry: { id, cat, label, img, name? }
//   id    → unique chip identifier
//   cat   → CATEGORIES key to activate
//   label → chip display text
//   img   → thumbnail
//   name  → sets _customName on category (for product-specific configurator heading)
//   hiddenFromSwitcher → keep legacy URL/cart identity without displaying a chip
var SWITCHER_GROUPS = {
  'custom-stickers': [
    { id: 'die-cut',      cat: 'custom-stickers', label: 'Die Cut',         img: (window.VP_CDN+"customstickers_diecutstickers.png?v=04c59469b7"),     name: 'Die Cut Stickers' },
    { id: 'transfer',     cat: 'custom-stickers', label: 'Kiss Cut + Transfer', img: (window.VP_CDN+"customstickers_transferstickers.png?v=905c8b97f6"),  name: 'Kiss Cut Transfer Stickers' },
    { id: 'circle',       cat: 'custom-stickers', label: 'Circle',          img: (window.VP_CDN+"customstickers_diecutstickers_1.png?v=de12002ae1"),   name: 'Circle Stickers' },
    { id: 'square',       cat: 'custom-stickers', label: 'Square',          img: (window.VP_CDN+"customstickers_squarestickers.png?v=8877fb15d4"),     name: 'Square Stickers' },
    { id: 'rectangle',    cat: 'custom-stickers', label: 'Rectangle',       img: (window.VP_CDN+"customstickers_rectanglestickers.png?v=66a3796301"), name: 'Rectangle Stickers', hiddenFromSwitcher: true },
    { id: 'sheets',       cat: 'custom-stickers', label: 'Sheets',          img: (window.VP_CDN+"customstickers_stickersheets.png?v=2355941751"),      name: 'Sticker Sheets' },
    { id: 'kiss-cut',     cat: 'custom-stickers', label: 'Kiss Cut',        img: (window.VP_CDN+"customstickers_kisscutstickers.png?v=23f432424a"),   name: 'Kisscut Stickers' },
    { id: 'clear',        cat: 'custom-stickers', label: 'Clear',           img: (window.VP_CDN+"customstickers_clearstickers.png?v=6496259fcc"),     name: 'Clear Stickers', hiddenFromSwitcher: true },
    { id: 'vinyl-letter', cat: 'custom-stickers', label: 'Vinyl Lettering', img: (window.VP_CDN+"customstickers_vinyllettering.png?v=f36241c6aa"),    name: 'Vinyl Lettering' },
    { id: 'static-cling', cat: 'custom-stickers', label: 'Static Cling',   img: (window.VP_CDN+"customstickers_staticcling.png?v=c406e763bf"),       name: 'Static Cling' },
    { id: 'front-adhes',  cat: 'custom-stickers', label: 'Front Adhesive', img: (window.VP_CDN+"customstickers_frontadhesive.png?v=37131e46ba"),     name: 'Front Adhesive' }
  ],
  'embossed-stickers': [
    { id: 'emb-die-cut',  cat: 'embossed-stickers', label: 'Die Cut',      img: (window.VP_CDN+"embossedstickers_EmbossedDieCut.png?v=0d9558a00b"),           name: 'Embossed Die Cut' },
    { id: 'emb-perf',     cat: 'embossed-stickers', label: 'Perforated',   img: (window.VP_CDN+"embossedstickers_embossedperforated.png?v=a4c5c3cdfd"),       name: 'Embossed Perforated' },
    { id: 'emb-circle',   cat: 'embossed-stickers', label: 'Circle',       img: (window.VP_CDN+"embossedstickers_embossedcirclestickers.png?v=3dc47fd267"),   name: 'Embossed Circle' },
    { id: 'emb-square',   cat: 'embossed-stickers', label: 'Square',       img: (window.VP_CDN+"embossedstickers_embossedsquarestickers.png?v=bedfe61714"),   name: 'Embossed Square' },
    { id: 'emb-rect',     cat: 'embossed-stickers', label: 'Rectangle',    img: (window.VP_CDN+"embossedstickers_embossedrectanglestickers.png?v=d107cbaec1"),name: 'Embossed Rectangle' },
    { id: 'emb-sheets',   cat: 'embossed-stickers', label: 'Sheets',       img: (window.VP_CDN+"embossedstickers_embossedstickersheets.png?v=92ffbcc075"),    name: 'Embossed Sticker Sheets' },
    { id: 'emb-clear',    cat: 'embossed-stickers', label: 'Clear',        img: (window.VP_CDN+"embossedstickers_embossedclearstickers.png?v=c93ce52137"),    name: 'Embossed Clear', hiddenFromSwitcher: true },
    { id: 'emb-holo',     cat: 'embossed-stickers', label: 'Holographic',  img: (window.VP_CDN+"embossedstickers_embossedholo.png?v=426763342a"),             name: 'Embossed Holographic', hiddenFromSwitcher: true }
  ],
  'labels': [
    { id: 'lbl-circle',   cat: 'labels', label: 'Circle',         img: (window.VP_CDN+"customlabels_circlesheet.png?v=9cde91f4b3"),      name: 'Circle Sheet' },
    { id: 'lbl-die-cut',  cat: 'labels', label: 'Die Cut',        img: (window.VP_CDN+"customlabels_diecutsheet.png?v=eb03b65d85"),      name: 'Die Cut Sheet' },
    { id: 'lbl-oval',     cat: 'labels', label: 'Oval',           img: (window.VP_CDN+"customlabels_ovalsheet.png?v=2db0ab5a59"),        name: 'Oval Sheet' },
    { id: 'lbl-rect',     cat: 'labels', label: 'Rectangle',      img: (window.VP_CDN+"customlabels_rectanglesheet.png?v=10410e1d98"),   name: 'Rectangle Sheet' },
    { id: 'lbl-rounded',  cat: 'labels', label: 'Rounded Corner', img: (window.VP_CDN+"customlabels_roundcornersheet.png?v=3f87142ef5"), name: 'Rounded Corner Sheet' },
    { id: 'lbl-square',   cat: 'labels', label: 'Square',         img: (window.VP_CDN+"customlabels_squaresheet.png?v=2bc756602d"),      name: 'Square Sheet' },
    { id: 'lbl-roll',     cat: 'labels', label: 'Roll Labels',    img: (window.VP_CDN+"customlabels_rolllabels.png?v=b42671f2fd"),       name: 'Roll Labels' }
  ],
  // 'packaging' is built dynamically from loadPackagingConfig() — see getChipsForContext()
  'packaging': null,
  'posters': [
    { id: 'posters',      cat: 'posters', label: 'Posters',    img: (window.VP_CDN+"postersnmore_posters.png?v=5cdca2b678"),  name: 'Posters' },
    { id: 'yard-signs',   cat: 'posters', label: 'Yard Signs', img: (window.VP_CDN+"postersnmore_yardsign.png?v=9566a51233"), name: 'Yard Signs' },
    { id: 'banners',      cat: 'posters', label: 'Banners',    img: (window.VP_CDN+"postersnmore_banners_vp_raw.png?v=f9e965b4e8"),  name: 'Banners' }
  ]
};

// Map from nav-highlight / cat value → group key
var NAV_TO_GROUP = {
  'custom-stickers':    'custom-stickers',
  'embossed-stickers':  'embossed-stickers',
  'specialty-stickers': 'embossed-stickers',
  'labels':             'labels',
  'packaging':          'packaging',
  'posters':            'posters'
};

// Per-product metadata for packaging products (image + routing for each known product name)
var PACKAGING_PRODUCT_META = {
  'Miron Jar Labels':  { cat: 'labels',    nav: 'packaging', img: (window.VP_CDN+"miron_side.svg?v=353c5e4127") },
  'Flower Jar Labels': { cat: 'labels',    nav: 'packaging', img: (window.VP_CDN+"flower_jar_side.svg?v=be1891f779") },
  'Mylar Bags':        { cat: 'packaging',                   img: (window.VP_CDN+"custompackaging_MylarBag.png?v=24ce28b3a0") },
  'Pre-Roll Tubes':    { cat: 'packaging',                   img: (window.VP_CDN+"custompackaging_PreRoll.png?v=6f95bbec99") }
};
var PACKAGING_FALLBACK_IMG = (window.VP_CDN+"navbar_icon.png?v=6b5a605354");

var currentChipId = null;

// ─── PER-PRODUCT STATE STORE ─────────────────────────────────────────────────
// Keyed by chip id: only the extras one product owns (package size, print
// finish, jar colour). Size / qty / material / laminate carry across switches
// (captureCarriedOptions); artwork and notes never come back (Josh 10/15).
var productStates = {};

function getNavContext() {
  return (typeof window._navHighlight !== 'undefined' ? window._navHighlight : null) || currentCat;
}

function getChipsForContext() {
  var ctx = getNavContext();
  var groupKey = NAV_TO_GROUP[ctx] || NAV_TO_GROUP[currentCat];
  if (!groupKey) return [];

  if (groupKey === 'packaging') {
    // Build dynamically from admin config
    var products = loadPackagingConfig().products || [];
    return products.map(function(prod) {
      var meta = PACKAGING_PRODUCT_META[prod.name] || {};
      return {
        id:    prod.id,
        name:  prod.name,
        cat:   meta.cat || 'packaging',
        nav:   meta.nav || 'packaging',
        label: prod.name,
        img:   meta.img || PACKAGING_FALLBACK_IMG
      };
    });
  }

  return SWITCHER_GROUPS[groupKey] || [];
}

function buildProductSwitcher() {
  var inner    = document.getElementById('psInner');
  var strip    = document.getElementById('productSwitcher');
  if (!inner || !strip) return;

  var chips = getChipsForContext();

  // Validate against the full catalog so hidden legacy products keep their identity.
  var ids = chips.map(function(c) { return c.id; });
  if (!currentChipId || ids.indexOf(currentChipId) === -1) currentChipId = chips.length ? chips[0].id : null;

  var visibleChips = chips.filter(function(chip) { return !chip.hiddenFromSwitcher; });
  if (getNavContext() === 'packaging') {
    visibleChips = visibleChips.concat([{ id: 'empty-jars-shop', label: 'Empty Jars', img: (window.VP_CDN+"jars_miron_saturn_1.jpg?v=b0b2c24bfe"), href: "/?view=jars" }]);
  }
  inner.innerHTML = '';
  // Hide the strip entirely when there's only one option (nothing to switch to)
  if (visibleChips.length <= 1) { strip.style.display = 'none'; return; }
  strip.style.display = '';

  visibleChips.forEach(function(chip) {
    var href = chip.href || (chip.name === 'Miron Jar Labels' ? "/?view=jar-labels&jar=miron-5ml-ceres" :
      chip.name === 'Flower Jar Labels' ? "/?view=jar-labels&jar=black-4oz" : '');
    var btn = document.createElement(href ? 'a' : 'button');
    if (href) btn.href = href;
    else { btn.type = 'button'; btn.setAttribute('aria-pressed', String(chip.id === currentChipId)); }
    btn.className = 'ps-chip' + (!href && chip.id === currentChipId ? ' active' : '');
    btn.dataset.chipId = chip.id;
    btn.innerHTML =
      '<img src="' + chip.img + '" alt="">' +
      '<span class="ps-chip-label">' + chip.label + '</span>';
    if (!href) btn.onclick = function() { switchProductChip(chip); };
    inner.appendChild(btn);
  });
}

// Drafts hold artwork references only in memory; cart storage is independent.
function captureProductArtwork() {
  function filename(selector) {
    var el = document.querySelector(selector);
    return el ? el.textContent : '';
  }
  return {
    body: artworkDataUrl, bodyOriginal: artworkOriginalDataUrl,
    lid: lidArtworkDataUrl, lidOriginal: lidArtworkOriginalDataUrl,
    back: mylarBackArtworkDataUrl, backOriginal: mylarBackOriginalDataUrl,
    bottom: mylarBottomArtworkDataUrl, bottomOriginal: mylarBottomOriginalDataUrl,
    file: artworkFile,
    bodyFilename: filename('[data-qa="upload-label"] strong'),
    lidFilename: filename('#lidUploadLabel strong'),
    backFilename: filename('#mylarBackLabel strong'),
    bottomFilename: filename('#mylarBottomLabel strong')
  };
}

function applyProductArtwork(art) {
  art = art || {};
  artworkDataUrl = art.body || null;
  artworkOriginalDataUrl = art.bodyOriginal || null;
  lidArtworkDataUrl = art.lid || null;
  lidArtworkOriginalDataUrl = art.lidOriginal || null;
  mylarBackArtworkDataUrl = art.back || null;
  mylarBackOriginalDataUrl = art.backOriginal || null;
  mylarBottomArtworkDataUrl = art.bottom || null;
  mylarBottomOriginalDataUrl = art.bottomOriginal || null;
  artworkFile = art.file || '(none)';
  [
    ['uploadPreview', '[data-qa="upload-label"]', art.body, art.bodyFilename],
    ['uploadPreviewLid', '#lidUploadLabel', art.lid, art.lidFilename],
    ['uploadPreviewMylarBack', '#mylarBackLabel', art.back, art.backFilename],
    ['uploadPreviewMylarBottom', '#mylarBottomLabel', art.bottom, art.bottomFilename]
  ].forEach(function(zone) {
    var preview = document.getElementById(zone[0]);
    if (preview) {
      if (zone[2]) preview.src = zone[2];
      else preview.removeAttribute('src');
      preview.style.display = zone[2] ? 'block' : 'none';
    }
    var label = document.querySelector(zone[1]);
    if (label) label.innerHTML = zone[2] ? '✓ <strong>' + escHtml(zone[3] || 'Artwork loaded') + '</strong>' : UPLOAD_LABEL_DEFAULT;
  });
  document.querySelectorAll('.upload-zone input[type="file"]').forEach(function(input) { input.value = ''; });
  setPreviewHint(!artworkDataUrl);
  var dlBtn = document.getElementById('printDlBtn');
  if (dlBtn) dlBtn.classList.toggle('visible', !!artworkDataUrl);
}

// ─── SAVE current product's per-product extras before switching away ──────────
// Josh 10/15 (Sue's testing): moving between products keeps the options that
// still apply (size, qty, material, laminate, embossing, carried by
// captureCarriedOptions) and NEVER brings back artwork, notes or white-ink
// areas. Only the extras one product owns (package size, print finish/sides,
// jar colour, die-cut backing) are remembered per product here.
function saveProductState() {
  if (!currentChipId) return;
  productStates[currentChipId] = {
    printFinish: currentPrintFinish, printSides: currentPrintSides,
    underlay: currentUnderlay, jarColor: currentJarColor,
    packageSizeId: currentPackageSize ? currentPackageSize.id : null,
    compareGroup: currentPackageSize ? currentPackageSize.compareGroup || null : null
  };
}

// The options a shopper keeps when they switch products (same set Add to Cart keeps).
function captureCarriedOptions() {
  function value(id) { var el = document.getElementById(id); return el ? el.value : ''; }
  return {
    printKind: typeof vpGetPrintKind === 'function' ? vpGetPrintKind() : null,
    packaging: !!(typeof getPackagingProduct === 'function' && getPackagingProduct()),
    sizeLabel: getSelectedSizeLabel(), customW: value('customW'), customH: value('customH'),
    qty: value('qtyInput'),
    materialId: MATERIALS[currentMat] ? MATERIALS[currentMat].id : null,
    finish: currentFinish, specialty: currentSpecialty, emboss: currentEmboss
  };
}

// ─── RESTORE after rebuilding the product's controls ───────────────────────────
function restoreProductState(chipId, productName, carry) {
  var saved = productStates[chipId] || null;
  var printKind = typeof vpGetPrintKind === 'function' ? vpGetPrintKind() : null;
  var packaging = !!(typeof getPackagingProduct === 'function' && getPackagingProduct());
  // Carry only between like products (stickers↔stickers, prints↔prints).
  if (carry && (!!carry.printKind !== !!printKind || carry.packaging !== packaging)) carry = null;

  if (carry && !packaging) {
    var sizes = document.querySelectorAll('#sqSizeList input[type="radio"]');
    var match = Array.from(sizes).find(function(radio) { return radio.value === carry.sizeLabel; });
    if (match) {
      sizes.forEach(function(radio) { radio.checked = radio === match; });
      if (match.value === 'Custom') {
        document.getElementById('customW').value = carry.customW == null ? '' : carry.customW;
        document.getElementById('customH').value = carry.customH == null ? '' : carry.customH;
      }
      onSizeChange(true);
    }
  }
  setQty(carry && Number(carry.qty) > 0 ? carry.qty : (printKind ? 1 : 50));

  if (printKind) {
    currentPrintFinish = (saved && saved.printFinish) || (printKind === 'banners' ? 'unhemmed-grommets' : 'satin');
    currentPrintSides = (saved && saved.printSides) || 'single';
    if (typeof updatePrintOptionButtons === 'function') updatePrintOptionButtons();
    if (typeof applyPrintPreviewFinish === 'function') applyPrintPreviewFinish();
  }
  if (saved && saved.underlay) currentUnderlay = saved.underlay;
  if (carry && !printKind) {
    var matIdx = MATERIALS.findIndex(function(mat) { return mat.id === carry.materialId; });
    if (matIdx >= 0) selectMaterial(matIdx);
    // The setters are exclusive: laminate first, then embossing (which the new product may refuse).
    setFinish(carry.finish === 'matte' ? 'matte' : 'glossy');
    setSpecialtyFinish(SPECIALTY_LABELS[carry.specialty] ? carry.specialty : 'none');
    setEmboss(carry.emboss || 'none');
  }
  if (currentCat === 'embossed-stickers' && currentEmboss === 'none') setEmboss('std');

  var pkg = getPackagingProduct();
  var sel = document.getElementById('packageSizeSelect');
  if (saved && pkg && sel) {
    var size = (pkg.sizes || []).find(function(sz) { return sz.id === saved.packageSizeId; });
    if (!size && saved.packageSizeId && saved.compareGroup) {
      size = (pkg.sizes || []).find(function(sz) { return sz.compareGroup === saved.compareGroup; });
    }
    sel.value = size ? size.id : '';
    onPackageSizeChange(pkg.name === 'Flower Jar Labels');
  }
  if (saved && saved.jarColor) {
    currentJarColor = saved.jarColor;
    if (productName === 'Flower Jar Labels') selectJarColor(saved.jarColor);
  }

  if (currentCat === 'labels' && isSheetLabelProduct()) renderSheetLabelPreview();
  else if ((currentCat === 'labels' || currentCat === 'packaging') && typeof preview3D !== 'undefined') {
    preview3D.activate(artworkDataUrl || mylarBackArtworkDataUrl || mylarBottomArtworkDataUrl);
  } else showArtworkPreview();
  updateSizeTrigger();
  updateQtyTrigger();
  updatePrice();
  updateCartButtonState();
}

// Josh 10/15: what Add to Cart and a product switch both wipe — artwork (every
// slot), Special instructions / white-ink / raised areas. Size, qty, material
// and laminate stay. `onlyIfNotes` skips the notes when the shopper already
// typed something new while the cart was saving.
function vpClearPerOrderInputs(onlyIfNotes) {
  if (typeof resetArtwork === 'function') resetArtwork();
  mylarBackArtworkDataUrl = null;
  mylarBackOriginalDataUrl = null;
  mylarBottomArtworkDataUrl = null;
  mylarBottomOriginalDataUrl = null;
  [['uploadPreviewMylarBack', '#mylarBackLabel'], ['uploadPreviewMylarBottom', '#mylarBottomLabel']].forEach(function(zone) {
    var preview = document.getElementById(zone[0]);
    if (preview) { preview.removeAttribute('src'); preview.style.display = 'none'; }
    var label = document.querySelector(zone[1]);
    if (label) label.innerHTML = UPLOAD_LABEL_DEFAULT;
  });
  var notes = document.getElementById('specialInstr');
  if (notes && (onlyIfNotes == null || notes.value === onlyIfNotes)) {
    notes.value = '';
    var acc = document.getElementById('instrAccordion');
    if (acc) acc.classList.remove('open');
    if (currentEdgeCut && typeof setEdgeCut === 'function') setEdgeCut(false);
  }
}

function switchProductChip(chip) {
  if (!chip || !CATEGORIES[chip.cat]) return;
  if (chip.id === currentChipId && chip.cat === currentCat) {
    closeSizeQtyPopup();
    return;
  }
  var carry = captureCarriedOptions();
  saveProductState();
  // Uploads still loading for the product being left can never land later.
  var leaving = typeof productArtworkUploads === 'object' && productArtworkUploads[productArtworkUploadKey()];
  if (leaving) Object.keys(leaving).forEach(function(slot) { delete leaving[slot]; });
  closeSizeQtyPopup();
  _cancelMaterialAnim();
  vpClearPerOrderInputs();
  document.getElementById('artworkOverlay').innerHTML = '';
  document.getElementById('customW').value = '';
  document.getElementById('customH').value = '';
  currentFinish = 'glossy';
  currentSpecialty = 'none';
  currentPrintFinish = 'satin';
  currentPrintSides = 'single';
  currentEmboss = 'none';
  currentJarColor = 'black';
  currentUnderlay = MATERIALS[CATEGORIES[chip.cat].defaultMat || 0].id;
  currentChipId = chip.id;

  var cat = CATEGORIES[chip.cat];
  delete cat._customName;
  delete cat._customImage;
  if (chip.name) cat._customName = chip.name;
  cat._customImage = chip.img;
  window._navHighlight = chip.nav || chip.cat;
  switchCategory(chip.cat);

  // Rebuilding after restoration would discard both preset and custom quantities.
  // The custom size/qty inputs sit inside these lists; park them before the wipe.
  sqParkCustom('sqCustomSize', document.getElementById('sqSizeList'));
  sqParkCustom('sqCustomQty', document.getElementById('sqQtyList'));
  document.getElementById('sqSizeList').innerHTML = '';
  document.getElementById('sqQtyList').innerHTML = '';
  buildSizeSelect();
  buildQtySelect();
  restoreProductState(chip.id, chip.name || null, carry);

  var activeEl = document.querySelector('.ps-chip.active');
  if (activeEl) activeEl.scrollIntoView({ inline: 'nearest', behavior: 'smooth', block: 'nearest' });
}

var CATEGORIES = {
  'custom-stickers': {
    name: 'CUSTOM STICKERS',
    image: (window.VP_CDN+"customstickers_HoloStickers.png?v=347c54e3e6"),
    // product image updates when material is picked
    materialImages: {
      'white-vinyl': (window.VP_CDN+"customstickers_diecutstickers.png?v=04c59469b7"),
      'clear':       (window.VP_CDN+"customstickers_clearstickers.png?v=6496259fcc"),
      'chrome':      (window.VP_CDN+"customstickers_ChromeGoldMetallic.png?v=1511bb64dd"),
      'gold':        (window.VP_CDN+"customstickers_ChromeGoldMetallic.png?v=1511bb64dd"),
      'reflective':  (window.VP_CDN+"customstickers_ReflectiveStickers.png?v=81f4b8ca7c"),
      'holo':        (window.VP_CDN+"customstickers_HoloStickers.png?v=347c54e3e6"),
      'glitter':     (window.VP_CDN+"customstickers_GlitterStickers.png?v=229c3c34e5")
    },
    defaultMat: 0, // white vinyl selected by default
    baseUnit: 3.70,
    unit: 'sticker'
  },
  'embossed-stickers': {
    name: 'EMBOSSED STICKERS',
    image: (window.VP_CDN+"embossedstickers_EmbossedDieCut.png?v=0d9558a00b"),
    materialImages: {
      'white-vinyl': (window.VP_CDN+"embossedstickers_EmbossedDieCut.png?v=0d9558a00b"),
      'clear':       (window.VP_CDN+"embossedstickers_embossedclearstickers.png?v=c93ce52137"),
      'chrome':      (window.VP_CDN+"embossedstickers_embossedholo.png?v=426763342a"),
      'gold':        (window.VP_CDN+"embossedstickers_embossedholo.png?v=426763342a"),
      'reflective':  (window.VP_CDN+"embossedstickers_embossedholo.png?v=426763342a"),
      'holo':        (window.VP_CDN+"embossedstickers_embossedholo.png?v=426763342a"),
      'glitter':     (window.VP_CDN+"embossedstickers_embossedstickersheets.png?v=92ffbcc075")
    },
    defaultMat: 0,
    baseUnit: 4.20,
    unit: 'sticker'
  },
  'labels': {
    name: 'CUSTOM LABELS',
    image: null,
    defaultMat: 0,
    baseUnit: 3.50,
    unit: 'label'
  },
  'packaging': {
    name: 'CUSTOM PACKAGING',
    image: (window.VP_CDN+"custompackaging_MylarBag.png?v=24ce28b3a0"),
    defaultMat: 0,
    baseUnit: 5.80,
    unit: 'unit'
  },
  'posters': {
    name: 'POSTERS & MORE',
    image: (window.VP_CDN+"postersnmore_posters.png?v=5cdca2b678"),
    defaultMat: 0,
    baseUnit: 6.50,
    unit: 'poster'
  }
};

// Default inner HTML for .upload-label when no file is selected.
// Used by all reset paths so the zone restores its full empty state.
var UPLOAD_LABEL_DEFAULT = '';

// Multipliers are NOT stored here -- pricing.materials (admin-configurable,
// see pricing-engine.js loadPricing()) is the source of truth; getMatMult()
// looks up the real mult by matching .id.
var MATERIALS = [
  { id: 'white-vinyl', label: 'White Vinyl' },
  { id: 'clear',       label: 'Clear' },
  { id: 'holo',        label: 'Holo' },
  { id: 'reflective',  label: 'Reflective' },
  { id: 'chrome',      label: 'Chrome' },
  { id: 'gold',        label: 'Gold' },
  { id: 'glitter',     label: 'Glitter' }
];

// Jar color options — shown only for Flower Jar Labels (body_wrap products)
var JAR_COLORS = [
  { id: 'black', label: 'Black', img: (window.VP_CDN+"flower_jar_side.svg?v=be1891f779") },
  { id: 'white', label: 'White', img: (window.VP_CDN+"flower_jar_white.svg?v=80af098345") },
  { id: 'clear', label: 'Clear', img: (window.VP_CDN+"flower_jar_clear.svg?v=6d55762b5d") }
];

var currentCat = 'custom-stickers';
var currentMat = 0; // index into MATERIALS
var currentFinish = 'glossy';
var currentSpecialty = 'none';
var currentPrintFinish = 'satin';
var currentPrintSides = 'single';
var currentEmboss = 'none'; // none | std | heavy — Command dropped Light; leftover light maps to std
var currentEdgeCut = false; // Special instructions "Cut directly to artwork edge" (Die Cut, Kiss Cut Transfer; Josh 10/17)
var EMBOSS_LABELS = { none: 'Flat', std: 'Standard', heavy: 'Heavy' };
// Specialty finish upcharge multipliers are NOT stored here -- they come from
// pricing.laminateUpcharges (admin-configurable, see vpGetMultipliers() in
// pricing-engine.js). Satin and all glitter-lam-* variants map to admin %
// fields (glitter-lam-* -> glitter, holo-lam-* -> holo); any other/unknown
// specialty value gets no upcharge (mult 1.0).
// Stocked finish names (Galactic Series = metal-flake overlaminate,
// Spectrum = holographic overlaminate, including independently sourced Hearts).
// They travel on the order
// line as "Specialty Finish" and cart-checkout.js maps label -> key on restore.
var SPECIALTY_LABELS = {
  'none':'More','satin':'Satin',
  // GALACTIC SERIES (metal flake)
  'glitter-lam-silver':'Apollo Silver','glitter-lam-gold':'Orion Gold',
  'glitter-lam-rainbow':'Cosmic Shift','glitter-lam-red':'Mars Red',
  'glitter-lam-neptune':'Neptune','glitter-lam-stardust':'Stardust',
  // SPECTRUM (holographic)
  'holo-lam-fracture':'Fracture','holo-lam-prismatic':'Prismatic',
  'holo-lam-cosmic':'Super Cosmic','holo-lam-hex':'Hex','holo-lam-hearts':'Hearts'
};
// Labels used before the Sep 2026 rename -- kept so saved carts still restore.
var SPECIALTY_LEGACY_LABELS = {
  'silver glitter':'glitter-lam-silver','gold glitter':'glitter-lam-gold',
  'rainbow glitter':'glitter-lam-rainbow'
};
var currentJarColor = 'black'; // 'black' | 'white' | 'clear'
var currentUnderlay = 'white'; // die cut backing material id

// Underlay options — keyed to match MATERIALS ids so selectMaterial() can sync them
var UNDERLAY_OPTIONS = [
  { id: 'white-vinyl', label: 'White',      color: '#ffffff' },
  { id: 'clear',       label: 'Clear',      pattern: 'checkerboard' },
  { id: 'chrome',      label: 'Chrome',     pattern: 'mirror' },
  { id: 'gold',        label: 'Gold',       pattern: 'gold' },
  { id: 'reflective',  label: 'Reflective', pattern: 'reflective' },
  { id: 'holo',        label: 'Holo',       pattern: 'holo' },
  { id: 'glitter',     label: 'Glitter',    pattern: 'glitter' },
];
// ── Cart: load from sessionStorage so it survives page-to-page navigation ──
var cartItems = (function() {
  try { return JSON.parse(sessionStorage.getItem('vp_cart') || '[]'); }
  catch(e) { return []; }
})();
var cartCount = cartItems.length;
var currentOrderRef = (function() { // shared order ref — persisted so page reloads don't lose it
  try {
    var stored = sessionStorage.getItem('vp_order_ref') || null;
    // Recovery: cart was restored from sessionStorage but order ref was never saved
    // (e.g. items added before vp_order_ref persistence was introduced).
    // Generate one now so checkout doesn't fall back to 'ORD-0000'.
    if (!stored && (JSON.parse(sessionStorage.getItem('vp_cart') || '[]').length > 0)) {
      stored = getNextOrderRef();
      sessionStorage.setItem('vp_order_ref', stored);
    }
    return stored;
  } catch(e) { return null; }
})();
var artworkDataUrl         = null;  // body artwork — PNG (may be PDF→canvas render)
var artworkOriginalDataUrl = null;  // original PDF data URL if PDF was uploaded (Drive receives this)
var lidArtworkDataUrl         = null;  // lid artwork — PNG (may be PDF→canvas render)
var lidArtworkOriginalDataUrl = null;  // original PDF data URL for lid if PDF was uploaded
var mylarBackArtworkDataUrl    = null;  // mylar back panel artwork
var mylarBottomArtworkDataUrl  = null;  // mylar bottom panel artwork
var mylarBackOriginalDataUrl   = null;  // original PDF for back panel
var mylarBottomOriginalDataUrl = null;  // original PDF for bottom panel
var artworkFile       = '(none)';// structured filename — used by downloadPrintFile()

// ── PDF.js preview renderer ──────────────────────────────────────────────────
// Renders page 1 of a PDF data URL to a canvas and resolves with a PNG data URL.
// Falls back to null on any error so the upload still works without a preview.
function renderPdfPage(pdfDataUrl) {
  // 10/12: pdf.js loads on the first PDF upload (vp-lazy.js), not with the page.
  if (typeof pdfjsLib === 'undefined' && window.vpLazy) {
    return window.vpLazy('pdfjs').then(function () { return renderPdfPage(pdfDataUrl); }, function () { return null; });
  }
  return new Promise(function(resolve) {
    try {
      if (typeof pdfjsLib === 'undefined') { resolve(null); return; }
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      pdfjsLib.getDocument(pdfDataUrl).promise.then(function(pdf) {
        pdf.getPage(1).then(function(page) {
          var viewport = page.getViewport({ scale: 1.5 });
          var canvas   = document.createElement('canvas');
          canvas.width  = viewport.width;
          canvas.height = viewport.height;
          page.render({ canvasContext: canvas.getContext('2d'), viewport: viewport })
            .promise.then(function() {
              resolve(canvas.toDataURL('image/png'));
            }).catch(function() { resolve(null); });
        }).catch(function() { resolve(null); });
      }).catch(function() { resolve(null); });
    } catch(e) { resolve(null); }
  });
}
var currentProductImageUrl = ''; // real product photo — never overwritten by artwork
var currentUploadTab  = 'body';  // 'body' | 'lid' | 'mylar-front' | 'mylar-back' | 'mylar-bottom'
var currentPackageSize = null;   // { id, label, sideW, sideH, topW, topH } when packaging product selected

// ─── PACKAGING PRODUCT SIZES ─────────────────────────────────────────────────
// Mirrors PACKAGING_DEFAULTS in admin.html — stored under vinylpac_packaging

var PACKAGING_DEFAULTS = {
  products: [
    { id: 'miron-jar-labels', name: 'Miron Jar Labels',
      sizes: [
        { id: '5ml',   label: '5ml',   sideW: 2.00, sideH: 1.50, topW: 0.75, topH: 0.75 },
        { id: '150ml', label: '150ml', sideW: 4.00, sideH: 2.50, topW: 1.75, topH: 1.75 }
      ],
      requiredFields: { packageSize: true }
    },
    { id: 'flower-jar-labels', name: 'Flower Jar Labels',
      sizes: [
        { id: '3oz', label: '3 oz', default: true, sideW: 3.50, sideH: 2.00, topW: 2.50, topH: 2.50 }
      ]
    },
    { id: 'mylar-bags', name: 'Mylar Bags',
      sizes: [
        { id: 'small',  label: 'Small (1g)',    compareGroup: '1g',   sideW: 2.00, sideH: 3.00, topW: 0, topH: 0 },
        { id: 'medium', label: 'Medium (3.5g)', compareGroup: '3.5g', sideW: 3.00, sideH: 4.00, topW: 0, topH: 0 },
        { id: 'large',  label: 'Large (7g)',    compareGroup: '7g',   sideW: 3.50, sideH: 5.00, topW: 0, topH: 0 }
      ],
      requiredFields: { packageSize: true, bodyArtwork: true }
    },
    { id: 'pre-roll-tubes', name: 'Pre-Roll Tubes',
      sizes: [
        { id: '109mm', label: '109mm', sideW: 1.75, sideH: 3.50, topW: 0.75, topH: 0.75 }
      ]
    }
  ]
};

// Mylar + pre-roll tubes are hidden until after launch (Josh, 10/10). Remove
// an id here to bring the product back everywhere the packaging config is read.
var PACKAGING_HIDDEN = { 'mylar-bags': 1, 'pre-roll-tubes': 1, 'Mylar Bags': 1, 'Pre-Roll Tubes': 1 };
function isPackagingHidden(prod) { return !!(prod && (PACKAGING_HIDDEN[prod.id] || PACKAGING_HIDDEN[prod.name])); }
function withoutHiddenPackaging(cfg) {
  return { products: (cfg.products || []).filter(function(p) { return !isPackagingHidden(p); }) };
}

function loadPackagingConfig() {
  var cfg = loadPackagingConfigRaw();
  var out = {};
  for (var k in cfg) out[k] = cfg[k];
  out.products = withoutHiddenPackaging(cfg).products;
  return out;
}

function loadPackagingConfigRaw() {
  try {
    var stored = localStorage.getItem('vinylpac_packaging');
    if (!stored) return PACKAGING_DEFAULTS;
    var cfg = JSON.parse(stored);
    // Strip stale customerName key — field was removed from the configurator UI.
    // Without this, old localStorage data keeps hasRequiredConfig=true, which
    // prevents the button from enabling even when every visible field is filled.
    (cfg.products || []).forEach(function(p) {
      if (p.requiredFields) delete p.requiredFields.customerName;
      if (p.name === 'Miron Jar Labels') p.requiredFields = Object.assign({}, p.requiredFields, { packageSize: true });
      if (p.name === 'Flower Jar Labels') (p.sizes || []).forEach(function(sz) { if (sz.id === '3oz') sz.default = true; });
    });
    return cfg;
  } catch(e) { return PACKAGING_DEFAULTS; }
}

// Returns the packaging product config for the current product, or null if not a packaging product.
function getPackagingProduct() {
  var cat = CATEGORIES[currentCat];
  if (!cat) return null;
  var productName = cat._customName || cat.name;
  var pkgConfig = loadPackagingConfig();
  for (var i = 0; i < pkgConfig.products.length; i++) {
    if (pkgConfig.products[i].name === productName) return pkgConfig.products[i];
  }
  return null;
}

function updatePackageSizeSection() {
  var section  = document.getElementById('packageSizeSection');
  var sizeWrap = document.getElementById('sizeSelectWrap');
  if (!section) return;

  var pkgProd = getPackagingProduct();
  var isPackaging = !!pkgProd;

  section.style.display = isPackaging ? '' : 'none';
  if (sizeWrap) sizeWrap.style.display = isPackaging ? 'none' : '';

  if (isPackaging) {
    var sel = document.getElementById('packageSizeSelect');
    sel.innerHTML = '<option value="">— Select size —</option>';
    var defaultId = null;
    (pkgProd.sizes || []).forEach(function(sz) {
      var opt = document.createElement('option');
      opt.value = sz.id;
      opt.textContent = sz.label;
      if (sz.compareGroup) opt.dataset.compareGroup = sz.compareGroup;
      sel.appendChild(opt);
      if (sz.default && !defaultId) defaultId = sz.id;
    });
    // Reset state, then auto-select default if one is configured
    currentPackageSize = null;
    document.getElementById('packageLabelInfo').style.display = 'none';
    if (defaultId) {
      sel.value = defaultId;
      onPackageSizeChange(pkgProd.name === 'Flower Jar Labels');
    }
    // Apply any field defaults (material, finish, jar color, spot finish)
    applyPackagingProductDefaults(pkgProd);
  }
  // Always re-evaluate button state when the packaging section refreshes
  updateCartButtonState();
}

function applyPackagingProductDefaults(pkgProd) {
  if (!pkgProd || !pkgProd.fieldDefaults) return;
  var fd = pkgProd.fieldDefaults;

  // Material — find index by id in MATERIALS array
  if (fd.material) {
    for (var mi = 0; mi < MATERIALS.length; mi++) {
      if (MATERIALS[mi].id === fd.material) { selectMaterial(mi); break; }
    }
  }

  // Finish
  if (fd.finish) { setFinish(fd.finish); }

  // Jar color
  if (fd.jarColor) { selectJarColor(fd.jarColor); }

  // Specialty finish
  if (fd.specialty !== undefined) { setSpecialtyFinish(fd.specialty); }
}

// ─── REQUIRED FIELD VALIDATION ───────────────────────────────────────────────

// Custom poster/banner size past the shop's max → the max as text, else ''.
function vpPrintTooBig(w, h) {
  // Josh 10/31: "Roll labels only go up to 9x9" -- a bigger custom roll gets the same "Max size" note as a poster.
  if (typeof vpQuoteCut === 'function' && vpQuoteCut() === 'roll') return Math.max(w, h) > 9 ? '9\u2033 \u00d7 9\u2033' : '';
  var kind = typeof vpGetPrintKind === 'function' ? vpGetPrintKind() : null;
  if (!kind || typeof vpPrintMaxDims !== 'function') return '';
  var max = vpPrintMaxDims(kind);
  if (Math.min(w, h) <= max.short && Math.max(w, h) <= max.long) return '';
  return max.short + '\u2033 \u00d7 ' + max.long + '\u2033';
}

function getPackagingRequiredMissing() {
  var pkgProd = getPackagingProduct();
  if (!pkgProd || !pkgProd.requiredFields) return [];
  var rf = pkgProd.requiredFields;
  var missing = [];

  if (rf.packageSize && !currentPackageSize) {
    missing.push(document.getElementById('packageSizeSelect'));
  }
  // material/finish/jarColor always have a selected value — they never block
  // Josh 10/16: artwork never blocks Add to cart or checkout; it can come after checkout.
  return missing.filter(Boolean);
}

function updateCartButtonState() {
  var btn = document.querySelector('.btn-atc');
  if (!btn) return;

  // Clear all req-missing indicators
  document.querySelectorAll('.req-missing').forEach(function(el) {
    el.classList.remove('req-missing');
  });

  var missing = [];
  var oversize = '';
  var printKind = typeof vpGetPrintKind === 'function' ? vpGetPrintKind() : null;
  var printMinQty = printKind && typeof vpPrintMinQty === 'function' ? vpPrintMinQty(printKind) : 0;

  // ── 1. Size ───────────────────────────────────────────────────────────────
  var pkgProd = getPackagingProduct();
  if (pkgProd) {
    // Packaging: validate configured requiredFields (package size, artwork, lid)
    var pkgMissing = getPackagingRequiredMissing();
    var hasReqFields = pkgProd.requiredFields && Object.keys(pkgProd.requiredFields).length > 0;
    if (hasReqFields) {
      pkgMissing.forEach(function(el) { missing.push(el); });
    }
  } else {
    // Stickers / labels: only need to validate when "Custom" size is chosen
    var sizeWrap = document.getElementById('sizeSelectWrap');
    var sizeVisible = sizeWrap && sizeWrap.style.display !== 'none';
    if (sizeVisible) {
      var sizeLabel = getSelectedSizeLabel();
      if (sizeLabel === 'Custom') {
        var w = parseFloat(document.getElementById('customW').value);
        var h = parseFloat(document.getElementById('customH').value);
        if (!isFinite(w) || w <= 0 || !isFinite(h) || h <= 0) {
          missing.push(document.getElementById('sqCustomSize'));
        } else if (vpPrintTooBig(w, h)) {
          oversize = vpPrintTooBig(w, h);
          missing.push(document.getElementById('sqCustomSize'));
        }
      }
    }
  }

  var customQty = document.getElementById('qtyCustomInput');
  if (customQty && typeof getSelectedQtyValue === 'function' && getSelectedQtyValue() === 'custom') {
    var quantity = Number(customQty.value);
    var badQty = printKind
      ? (!customQty.value || !isFinite(quantity) || quantity < printMinQty || quantity % 1 !== 0)
      : (!customQty.value || !isFinite(quantity) || quantity < 20 || quantity % 10 !== 0);
    if (badQty) missing.push(customQty);
  }

  if (typeof updateSizeValidation === 'function') updateSizeValidation();
  if (typeof updateQtyValidation === 'function') updateQtyValidation();

  // ── 2. Artwork ────────────────────────────────────────────────────────────
  // Josh 10/16: "consistently on all pages we want to be able to add to cart and checkout without
  // uploading" — no file is fine; the button says so (same words as jar labels, 10/15).

  // Apply visual indicators and update button. Custom size / qty entries sit
  // inside the open list and get no outline; the note below names them.
  var validMissing = missing.filter(Boolean);
  validMissing.forEach(function(el) {
    if (el.id !== 'sqCustomSize' && el.id !== 'qtyCustomInput') el.classList.add('req-missing');
  });
  var quoteSpec = typeof vpQuoteSpec === 'function' ? vpQuoteSpec() : null;
  var quote = window._vpLastQuote;
  var quotePending = !!quoteSpec && (!quote || quote.key !== vpQuoteKey(quoteSpec) || quote.each == null || !isFinite(Number(quote.each)));
  var saving = typeof _cartSaving !== 'undefined' && _cartSaving;
  var checkingOut = typeof _cartCheckingOut !== 'undefined' && _cartCheckingOut;
  var restoring = typeof _cartEditing !== 'undefined' && _cartEditing && _cartEditing.phase !== 'ready';
  var uploading = typeof hasPendingProductArtwork === 'function' && hasPendingProductArtwork();
  var cartBusy = !!(saving || checkingOut || restoring || uploading);
  btn.disabled = validMissing.length > 0 || quotePending || cartBusy;
  if (!(typeof _cartEditing !== 'undefined' && _cartEditing)) btn.textContent = artworkDataUrl ? 'Add to cart' : 'Upload later and add to cart';
  btn.setAttribute('aria-busy', quotePending || cartBusy ? 'true' : 'false');
  btn.title = cartBusy
    ? (uploading ? 'Loading your artwork…' : saving ? 'Saving your artwork…' : checkingOut ? 'Preparing checkout…' : 'Loading your saved artwork…')
    : validMissing.length > 0 ? 'Please complete: ' + validMissing.map(function(el) {
        if (el.id === 'uploadZoneBody') return 'upload artwork';
        if (el.id === 'sqCustomSize') return oversize ? 'keep the size within ' + oversize : 'enter custom size';
        if (el.id === 'qtyCustomInput') return printKind ? 'enter a quantity of at least ' + printMinQty : 'enter a quantity of at least 20 in multiples of 10';
        if (el.id === 'packageSizeSelect') return 'select a size';
        if (el.id === 'uploadZoneLid') return 'upload lid artwork';
        return 'required field';
      }).join(', ')
    : (quotePending ? 'Updating your price…' : '');
  // Josh 10/09: no text under the button. A missing step shows on the
  // greyed button itself; the status line stays for screen readers only.
  var hint = '';
  if (!cartBusy && validMissing.length > 0) {
    var first = validMissing[0].id;
    hint = first === 'uploadZoneBody' ? 'Upload your artwork to continue'
      : first === 'sqCustomSize' ? (oversize ? 'Max size ' + oversize : 'Enter your custom size')
      : first === 'qtyCustomInput' ? (printKind ? 'Enter a quantity (' + printMinQty + '+)' : 'Enter a quantity (20+, in tens)')
      : first === 'packageSizeSelect' ? 'Select a size'
      : first === 'uploadZoneLid' ? 'Upload your lid artwork'
      : 'Finish the options above';
  }
  if (hint) btn.setAttribute('data-hint', hint); else btn.removeAttribute('data-hint');
  var message = document.getElementById('cartReadyMessage');
  if (message) message.textContent = btn.title || 'Ready to add this item.';
}

function onPackageSizeChange(hideSizeInfo) {
  var sel   = document.getElementById('packageSizeSelect');
  var selId = sel.value;
  var pkgProd = getPackagingProduct();
  // Flower has one size, including when restoring an older blank selection.
  if (!selId && pkgProd && pkgProd.name === 'Flower Jar Labels') { selId = '3oz'; sel.value = selId; hideSizeInfo = true; }
  var info  = document.getElementById('packageLabelInfo');

  if (!selId) {
    currentPackageSize = null;
    if (info) info.style.display = 'none';
    updatePrice();
    return;
  }

  if (!pkgProd) return;
  currentPackageSize = null;
  for (var i = 0; i < pkgProd.sizes.length; i++) {
    if (pkgProd.sizes[i].id === selId) { currentPackageSize = pkgProd.sizes[i]; break; }
  }

  if (currentPackageSize && info) {
    // Automatic Flower selection keeps the existing card height.
    info.style.display = hideSizeInfo === true ? 'none' : '';
    document.getElementById('pkgSideLabelSize').textContent =
      currentPackageSize.sideW + '\u2033 \xd7 ' + currentPackageSize.sideH + '\u2033';
    var topEl = document.getElementById('pkgTopLabelSize');
    if (currentPackageSize.topW && currentPackageSize.topH) {
      topEl.textContent = currentPackageSize.topW + '\u2033 \xd7 ' + currentPackageSize.topH + '\u2033';
      topEl.className = 'pkg-size-info-value';
    } else {
      topEl.textContent = 'N/A';
      topEl.className = 'pkg-size-na';
    }
  }
  updatePrice();
  updateCartButtonState();
}
